import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import {
  Plus,
  RefreshCw,
  FileText,
  Globe,
  Clock,
  Trash2,
  Edit3,
  Eye,
  MoreVertical,
  Search,
  Archive,
  CheckCircle2,
  AlertCircle,
  X,
} from 'lucide-react';
import {
  useAdminPagesList,
  deleteCmsPage,
  publishCmsPage,
  unpublishCmsPage,
} from '../hooks/useAdminPages';
import { CMS_TEMPLATES, type CmsTemplate } from '../components/CmsTemplates';
import { isSlugReserved, isSlugValid } from '@/types/cms';
import type { CmsPageSummary } from '@/types/cms';

type ModalState = 'closed' | 'template' | 'slug' | 'delete';

export default function CmsPagesPage() {
  const navigate = useNavigate();
  const { pages, isLoading, error, refetch } = useAdminPagesList();
  const [search, setSearch] = useState('');
  const [modalState, setModalState] = useState<ModalState>('closed');
  const [selectedTemplate, setSelectedTemplate] = useState<CmsTemplate | null>(null);
  const [newSlug, setNewSlug] = useState('');
  const [slugError, setSlugError] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CmsPageSummary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [actionMenuId, setActionMenuId] = useState<string | null>(null);

  // Stats
  const published = pages.filter((p) => p.status === 'published').length;
  const drafts = pages.filter((p) => p.status === 'draft').length;
  const archived = pages.filter((p) => p.status === 'archived').length;

  // Filter
  const filtered = pages.filter((p) => {
    const q = search.toLowerCase();
    return (
      p.title_en.toLowerCase().includes(q) ||
      p.title_fr.toLowerCase().includes(q) ||
      p.slug.toLowerCase().includes(q)
    );
  });

  // ── Create flow ──
  const handleSelectTemplate = (tpl: CmsTemplate) => {
    setSelectedTemplate(tpl);
    setNewSlug('');
    setSlugError('');
    setModalState('slug');
  };

  const handleSlugChange = (value: string) => {
    const sanitized = value
      .toLowerCase()
      .replace(/[^a-z0-9/-]/g, '')
      .replace(/\/+/g, '/')
      .replace(/^\//, '');
    setNewSlug(sanitized);
    setSlugError('');

    if (sanitized && !isSlugValid(sanitized)) {
      setSlugError('Use lowercase letters, numbers, and hyphens. Separate paths with /');
    } else if (isSlugReserved(sanitized)) {
      setSlugError('This path conflicts with an existing app route');
    }
  };

  const handleCreate = async () => {
    if (!selectedTemplate || !newSlug || slugError) return;
    setCreating(true);
    try {
      const { createCmsPage } = await import('../hooks/useAdminPages');
      const page = await createCmsPage({
        slug: newSlug,
        template: selectedTemplate.id,
        title_en: selectedTemplate.title_en,
        title_fr: selectedTemplate.title_fr,
        subtitle_en: selectedTemplate.subtitle_en,
        subtitle_fr: selectedTemplate.subtitle_fr,
        content_en: selectedTemplate.content_en,
        content_fr: selectedTemplate.content_fr,
      });
      setModalState('closed');
      navigate(`/admin/pages/${page.id}/edit`);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '';
      if (msg.includes('duplicate') || msg.includes('unique')) {
        setSlugError('This path is already in use');
      } else {
        setSlugError(msg || 'Failed to create page');
      }
    } finally {
      setCreating(false);
    }
  };

  // ── Delete flow ──
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteCmsPage(deleteTarget.id);
      setModalState('closed');
      setDeleteTarget(null);
      refetch();
    } catch {
      // silent
    } finally {
      setDeleting(false);
    }
  };

  // ── Publish / Unpublish ──
  const handleTogglePublish = async (page: CmsPageSummary) => {
    try {
      if (page.status === 'published') {
        await unpublishCmsPage(page.id);
      } else {
        await publishCmsPage(page.id);
      }
      refetch();
    } catch {
      // silent
    }
    setActionMenuId(null);
  };

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      published: 'bg-[#16c47f]/10 text-[#16c47f]',
      draft: 'bg-amber-100 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400',
      archived: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
    };
    const icons: Record<string, typeof CheckCircle2> = {
      published: CheckCircle2,
      draft: Clock,
      archived: Archive,
    };
    const Icon = icons[status] || Clock;
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${styles[status] || ''}`}
      >
        <Icon className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <AdminLayout
      title="CMS Pages"
      subtitle="Create and manage content pages for your platform"
      action={
        <div className="flex items-center gap-2">
          <button
            onClick={refetch}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-200 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={() => setModalState('template')}
            className="flex items-center gap-2 px-4 py-2 bg-[#16c47f] hover:bg-[#12a868] text-white rounded-xl text-sm font-semibold transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            New Page
          </button>
        </div>
      }
    >
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Pages', value: pages.length, color: 'text-gray-900 dark:text-white' },
          { label: 'Published', value: published, color: 'text-[#16c47f]' },
          { label: 'Drafts', value: drafts, color: 'text-amber-500' },
          { label: 'Archived', value: archived, color: 'text-gray-500' },
        ].map((stat) => (
          <div
            key={stat.label}
            className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5"
          >
            <p className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</p>
            <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Pages section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-2">
            <FileText className="w-4 h-4" />
            All Pages
          </h2>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search pages..."
              className="pl-9 pr-4 py-2 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-[#16c47f] focus:border-transparent outline-none w-64 transition-all"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-12 text-center">
            <RefreshCw className="w-6 h-6 text-gray-400 animate-spin mx-auto mb-2" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading pages...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 dark:bg-red-900/10 rounded-2xl border border-red-200 dark:border-red-800 p-6 text-center">
            <AlertCircle className="w-6 h-6 text-red-500 mx-auto mb-2" />
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-12 text-center">
            <FileText className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400 font-medium mb-1">
              {search ? 'No pages match your search' : 'No custom pages yet'}
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mb-4">
              {search ? 'Try a different search term' : 'Create your first page to get started'}
            </p>
            {!search && (
              <button
                onClick={() => setModalState('template')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#16c47f] hover:bg-[#12a868] text-white rounded-xl text-sm font-semibold transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create First Page
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 overflow-visible">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700">
                  <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-5 py-3">
                    Page
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-5 py-3">
                    Path
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-5 py-3">
                    Status
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-5 py-3">
                    Template
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-5 py-3">
                    Updated
                  </th>
                  <th className="text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-5 py-3">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((page) => (
                  <tr
                    key={page.id}
                    className="border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                  >
                    <td className="px-5 py-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {page.title_en || page.title_fr || 'Untitled'}
                          </p>
                          {page.is_system && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400">
                              <Globe className="w-2.5 h-2.5" />
                              System
                            </span>
                          )}
                        </div>
                        {page.title_fr && page.title_en && (
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                            {page.title_fr}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <code className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded-md">
                        /p/{page.slug}
                      </code>
                    </td>
                    <td className="px-5 py-4">{statusBadge(page.status)}</td>
                    <td className="px-5 py-4">
                      <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                        {page.template}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(page.updated_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1 relative">
                        <button
                          onClick={() => navigate(`/admin/pages/${page.id}/edit`)}
                          className="p-2 rounded-lg text-gray-400 hover:text-[#16c47f] hover:bg-[#16c47f]/5 transition-all"
                          title="Edit"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        {page.status === 'published' && (
                          <a
                            href={`/p/${page.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 rounded-lg text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-500/10 transition-all"
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </a>
                        )}
                        <div className="relative">
                          <button
                            onClick={() =>
                              setActionMenuId(actionMenuId === page.id ? null : page.id)
                            }
                            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          {actionMenuId === page.id && (
                            <div className="absolute right-0 top-full mt-1 w-44 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/50 shadow-lg z-50 py-1.5 animate-in fade-in slide-in-from-top-1 duration-150">
                              <button
                                onClick={() => handleTogglePublish(page)}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                              >
                                {page.status === 'published' ? (
                                  <>
                                    <Clock className="w-4 h-4" /> Unpublish
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle2 className="w-4 h-4 text-[#16c47f]" /> Publish
                                  </>
                                )}
                              </button>
                              {!page.is_system && (
                                <button
                                  onClick={() => {
                                    setDeleteTarget(page);
                                    setModalState('delete');
                                    setActionMenuId(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 flex items-center gap-2"
                                >
                                  <Trash2 className="w-4 h-4" /> Delete
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ═══ Template Selection Modal ═══ */}
      {modalState === 'template' && (
        <div
          className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4"
          onClick={() => setModalState('closed')}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Create New Page</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Choose a template to get started
                </p>
              </div>
              <button
                onClick={() => setModalState('closed')}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {CMS_TEMPLATES.map((tpl) => {
                  const Icon = tpl.icon;
                  return (
                    <button
                      key={tpl.id}
                      onClick={() => handleSelectTemplate(tpl)}
                      className="text-left p-4 rounded-xl border-2 border-gray-100 dark:border-gray-700 hover:border-[#16c47f]/50 dark:hover:border-[#16c47f]/30 hover:bg-[#16c47f]/5 dark:hover:bg-[#16c47f]/5 transition-all group"
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: tpl.color + '15' }}
                        >
                          <Icon className="w-5 h-5" style={{ color: tpl.color }} />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-[#16c47f] transition-colors">
                            {tpl.name_en}
                          </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500">{tpl.name_fr}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {tpl.description_en}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Slug Input Modal ═══ */}
      {modalState === 'slug' && selectedTemplate && (
        <div
          className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4"
          onClick={() => setModalState('template')}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-3 mb-1">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: selectedTemplate.color + '15' }}
                >
                  <selectedTemplate.icon
                    className="w-4 h-4"
                    style={{ color: selectedTemplate.color }}
                  />
                </div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Set Page Path</h2>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Template: {selectedTemplate.name_en}
              </p>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Page URL Path
              </label>
              <div className="flex items-center gap-0">
                <span className="text-sm text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-700 px-3 py-2.5 rounded-l-xl border border-r-0 border-gray-200 dark:border-gray-600 select-none">
                  /p/
                </span>
                <input
                  type="text"
                  value={newSlug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  placeholder="about, faq, blog/welcome..."
                  className="flex-1 px-3 py-2.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-r-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-[#16c47f] focus:border-transparent outline-none transition-all"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newSlug && !slugError) handleCreate();
                  }}
                />
              </div>
              {slugError && (
                <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {slugError}
                </p>
              )}
              {newSlug && !slugError && (
                <p className="text-xs text-[#16c47f] mt-2 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Your page will be at: /p/{newSlug}
                </p>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <button
                onClick={() => setModalState('template')}
                className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleCreate}
                disabled={!newSlug || !!slugError || creating}
                className="flex items-center gap-2 px-5 py-2 bg-[#16c47f] hover:bg-[#12a868] disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white disabled:text-gray-500 rounded-xl text-sm font-semibold transition-colors"
              >
                {creating ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Create Page
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Delete Confirmation Modal ═══ */}
      {modalState === 'delete' && deleteTarget && (
        <div
          className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4"
          onClick={() => {
            setModalState('closed');
            setDeleteTarget(null);
          }}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-2xl max-w-sm w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 text-center">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Delete Page</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                Are you sure you want to delete
              </p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                "{deleteTarget.title_en || deleteTarget.title_fr || deleteTarget.slug}"
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500">
                This action cannot be undone.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end gap-2">
              <button
                onClick={() => {
                  setModalState('closed');
                  setDeleteTarget(null);
                }}
                className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-semibold transition-colors"
              >
                {deleting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
